/* === Scroll Animations === */
const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

/* NEW: default cascade step (seconds) used by CSS var if data-cascade is set */
const DEFAULT_CASCADE_STEP_S = 0.08;

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;

      // If using cascade, set the order index and step var (CSS reads these)
      if (elementTarget.hasAttribute('data-cascade')) {
        elementTarget.style.setProperty('--animation-order', index);
        const stepAttr = elementTarget.getAttribute('data-cascade-step');
        const step = stepAttr ? parseFloat(stepAttr) : DEFAULT_CASCADE_STEP_S;
        if (!isNaN(step)) {
          elementTarget.style.setProperty('--cascade-step', `${step}s`);
        }
      }

      // Reveal (remove offscreen)
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      }

      // Stop observing unless explicitly allowed to replay
      if (elementTarget.getAttribute('data-scroll-once') !== 'false') {
        observer.unobserve(elementTarget);
      }
    } else {
      // Hide while offscreen (unless canceled)
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(
    rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME)
  );
  if (animationTriggerElements.length === 0) return;

  // Respect reduced motion: reveal immediately and bail
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    animationTriggerElements.forEach((el) => {
      el.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      el.classList.add('scroll-trigger--reduced-motion');
    });
    return;
  }

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  // Start all as offscreen (hidden) so CSS can animate them in
  animationTriggerElements.forEach((el) => {
    // Allow replay: if data-scroll-once="false" we keep observing; otherwise unobserve after first reveal.
    el.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
  });

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
    threshold: 0.15
  });

  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic (parallax zoom-on-scroll feel)
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(
    document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME)
  );
  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;
        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    return 0; // not reached yet
  } else if (elementPositionY + elementHeight < scrollY) {
    return 100; // fully passed
  }

  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) =>
    initializeScrollAnimationTrigger(event.target, true)
  );
  document.addEventListener('shopify:section:reorder', () =>
    initializeScrollAnimationTrigger(document, true)
  );
}
/* === Staggered reveal (class-only: .reveal-text / .reveal-words) === */
(function () {
  if (window.__classRevealInit) return;
  window.__classRevealInit = true;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function splitChars(el) {
    const text = el.textContent;
    el.setAttribute('aria-label', text.trim());
    el.setAttribute('role', 'text');
    el.textContent = '';
    let i = 0;
    for (const ch of text) {
      if (ch === ' ') { el.appendChild(document.createTextNode(' ')); continue; }
      const span = document.createElement('span');
      span.className = 'char';
      span.style.setProperty('--i', i++);
      span.textContent = ch;
      el.appendChild(span);
    }
  }

  function splitWords(el) {
    const text = el.textContent;
    el.setAttribute('aria-label', text.trim());
    el.setAttribute('role', 'text');
    el.textContent = '';
    let i = 0;
    text.split(/(\s+)/).forEach(token => {
      if (token.trim() === '') { el.appendChild(document.createTextNode(token)); return; }
      const span = document.createElement('span');
      span.className = 'word';
      span.style.setProperty('--i', i++);
      span.textContent = token;
      el.appendChild(span);
    });
  }

  function setup(root = document) {
    const targets = [
      ...root.querySelectorAll('.reveal-text'),
      ...root.querySelectorAll('.reveal-words')
    ];
    if (!targets.length) return;

    targets.forEach(el => {
      if (el.__splitDone) return;
      if (el.classList.contains('reveal-words')) splitWords(el);
      else splitChars(el);
      el.__splitDone = true;
      if (REDUCED) el.classList.add('is-visible');
    });

    if (REDUCED) return;

    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target); // play once; remove this line if you want replay
      });
    }, { threshold: 0.2 });

    targets.forEach(el => io.observe(el));
  }

  document.addEventListener('DOMContentLoaded', () => setup(document));

  // Shopify Theme Editor support
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', e => setup(e.target));
    document.addEventListener('shopify:section:reorder', () => setup(document));
  }
})();
